/**
 * <ss-configurator> — 5-step guided quiz for SmartSenses
 * Spec ref: Plan 5; data: assets/smart-glasses-data.json
 *
 * Steps:
 *  1. What are you looking for? (use case)
 *  2. What matters most? (priority)
 *  3. Must-have features (multi-select)
 *  4. Budget (€)
 *  5. Prescription / delivery time
 *
 * Output: top 3 ranked products (sellable only by default; toggle to widen)
 */
// i18n string accessor — falls back to English defaults if SsI18n missing.
const T = (key, vars) => {
  const dict = (window.SsI18n && window.SsI18n.configurator) || {};
  let s = dict[key] || key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      s = s.replace(new RegExp('\\{\\{\\s*' + k + '\\s*\\}\\}', 'g'), v);
    }
  }
  return s;
};

class SsConfigurator extends HTMLElement {
  async connectedCallback() {
    this.state = {
      step: 0, // 0-indexed; 5 = result
      useCase: null,
      primaryPriority: null,
      mustHave: { camera: false, ai: false, translation: false, waterproof: false, rx: false },
      budgetMax: 500,
      includePreorder: true,
      onlySellable: true,
    };

    if (!window.SsProducts) {
      this.innerHTML = '<p class="ss-configurator__error">Produktdaten-Modul nicht geladen.</p>';
      return;
    }
    try {
      await window.SsProducts.load();
    } catch (e) {
      this.innerHTML = `<p class="ss-configurator__error">Fehler: ${e.message}</p>`;
      return;
    }
    this.render();
  }

  render() {
    const total = 5;
    const step = this.state.step;
    let body = '';
    if (step === 0) body = this.renderStep0();
    else if (step === 1) body = this.renderStep1();
    else if (step === 2) body = this.renderStep2();
    else if (step === 3) body = this.renderStep3();
    else if (step === 4) body = this.renderStep4();
    else body = this.renderResult();

    this.innerHTML = `
      ${step < 5 ? `<div class="ss-configurator__progress"><span>${T('step', { current: step + 1, total })}</span><div class="ss-configurator__bar"><div class="ss-configurator__bar-fill" style="width:${((step + 1) / total) * 100}%"></div></div></div>` : ''}
      <div class="ss-configurator__step">${body}</div>
      ${step < 5 ? this.renderNav(step, total) : ''}
    `;
    this.wireEvents();
  }

  renderStep0() {
    const sel = this.state.useCase;
    const opts = [
      { v: 'commute',    l: T('use_case_commute'),    d: T('use_case_commute_desc') },
      { v: 'sport',      l: T('use_case_sport'),      d: T('use_case_sport_desc') },
      { v: 'motorcycle', l: T('use_case_motorcycle'), d: T('use_case_motorcycle_desc') },
      { v: 'tourism',    l: T('use_case_tourism'),    d: T('use_case_tourism_desc') },
      { v: 'work',       l: T('use_case_work'),       d: T('use_case_work_desc') },
      { v: 'travel',     l: T('use_case_travel'),     d: T('use_case_travel_desc') },
      { v: 'industry',   l: T('use_case_industry'),   d: T('use_case_industry_desc') },
    ];
    return `
      <h2 class="ss-display ss-display-m">${T('step0_title')}</h2>
      <p class="ss-configurator__sub">${T('step0_subtitle')}</p>
      <div class="ss-configurator__grid">
        ${opts.map((o) => `
          <button type="button" class="ss-configurator__card${sel === o.v ? ' is-selected' : ''}" data-set="useCase" data-value="${o.v}">
            <strong>${o.l}</strong>
            <span>${o.d}</span>
          </button>
        `).join('')}
      </div>
    `;
  }

  renderStep1() {
    const sel = this.state.primaryPriority;
    const opts = [
      { v: 'camera',      l: T('priority_camera') },
      { v: 'translation', l: T('priority_translation') },
      { v: 'discreet',    l: T('priority_discreet') },
      { v: 'budget',      l: T('priority_budget') },
      { v: 'display',     l: T('priority_display') },
    ];
    return `
      <h2 class="ss-display ss-display-m">${T('step1_title')}</h2>
      <p class="ss-configurator__sub">${T('step1_subtitle')}</p>
      <div class="ss-configurator__grid">
        ${opts.map((o) => `
          <button type="button" class="ss-configurator__card${sel === o.v ? ' is-selected' : ''}" data-set="primaryPriority" data-value="${o.v}">
            <strong>${o.l}</strong>
          </button>
        `).join('')}
      </div>
    `;
  }

  renderStep2() {
    const m = this.state.mustHave;
    return `
      <h2 class="ss-display ss-display-m">${T('step2_title')}</h2>
      <p class="ss-configurator__sub">${T('step2_subtitle')}</p>
      <div class="ss-configurator__checks">
        ${[
          ['camera', T('feature_camera')], ['ai', T('feature_ai')], ['translation', T('feature_translation')],
          ['waterproof', T('feature_waterproof')], ['rx', T('feature_rx')]
        ].map(([k, l]) => `
          <label class="ss-configurator__check">
            <input type="checkbox" data-musthave="${k}" ${m[k] ? 'checked' : ''}>
            <span>${l}</span>
          </label>
        `).join('')}
      </div>
    `;
  }

  renderStep3() {
    const v = this.state.budgetMax;
    return `
      <h2 class="ss-display ss-display-m">${T('step3_title')}</h2>
      <p class="ss-configurator__sub">${T('step3_subtitle')}</p>
      <div class="ss-configurator__slider">
        <input type="range" min="50" max="2500" step="10" value="${v}" data-budget>
        <div class="ss-configurator__slider-value">${T('budget_up_to_html', { amount: v })}</div>
      </div>
      <div class="ss-configurator__quick">
        ${[100, 200, 500, 1000].map((p) => `<button type="button" data-budget-quick="${p}">€${p}</button>`).join('')}
      </div>
    `;
  }

  renderStep4() {
    return `
      <h2 class="ss-display ss-display-m">${T('step4_title')}</h2>
      <p class="ss-configurator__sub">${T('step4_subtitle')}</p>
      <div class="ss-configurator__checks">
        <label class="ss-configurator__check">
          <input type="checkbox" data-set="includePreorder" ${this.state.includePreorder ? 'checked' : ''}>
          <span>${T('preorder_ok')}</span>
        </label>
        <label class="ss-configurator__check">
          <input type="checkbox" data-set="onlySellable" ${this.state.onlySellable ? 'checked' : ''}>
          <span>${T('only_smartsenses')}</span>
        </label>
      </div>
    `;
  }

  renderResult() {
    const results = window.SsProducts.score(
      {
        useCase: this.state.useCase,
        primaryPriority: this.state.primaryPriority,
        mustHave: this.state.mustHave,
        budgetEur: { min: 0, max: this.state.budgetMax },
      },
      { limit: 3, onlySellable: this.state.onlySellable }
    );

    if (!results.length) {
      return `
        <h2 class="ss-display ss-display-m">${T('no_results_title')}</h2>
        <p class="ss-configurator__sub">${
          this.state.onlySellable ? T('no_results_widen') : T('no_results_adjust')
        }</p>
        ${this.state.onlySellable
          ? `<button type="button" class="ss-button" data-action="widen">${T('include_full_market')}</button>`
          : `<button type="button" class="ss-button ss-button--ghost" data-action="restart">${T('configure_again')}</button>`}
      `;
    }

    const titleText = results.length === 1
      ? T('your_recommendation')
      : T('your_top', { count: results.length });

    return `
      <h2 class="ss-display ss-display-m">${titleText}</h2>
      <p class="ss-configurator__sub">${T('sorted_by_match')}</p>
      <ol class="ss-configurator__results">
        ${results.map((p, i) => this.renderResultCard(p, i)).join('')}
      </ol>
      <div class="ss-configurator__after">
        <button type="button" class="ss-button ss-button--ghost" data-action="restart">${T('configure_again')}</button>
        <a class="ss-link" href="/pages/vergleich">${T('compare_all')}</a>
      </div>
    `;
  }

  renderResultCard(p, i) {
    const eur = window.SsProducts.priceEUR(p);
    const sellable = p.sellable_at_smartsenses;
    const reasons = (p._reasons || []).slice(0, 3);
    return `
      <li class="ss-configurator__result">
        <div class="ss-configurator__result-rank">#${i + 1}</div>
        <div class="ss-configurator__result-body">
          <h3 class="ss-configurator__result-title">${this.esc(p.brand || '')} ${this.esc(p.model || '')}</h3>
          <p class="ss-configurator__result-price">${eur != null ? `€${eur}` : T('price_on_request')}</p>
          ${reasons.length ? `<ul class="ss-configurator__result-reasons">${reasons.map((r) => `<li>${this.esc(r)}</li>`).join('')}</ul>` : ''}
          ${p.notes ? `<p class="ss-configurator__result-notes">${this.esc(String(p.notes).slice(0, 180))}${p.notes.length > 180 ? '…' : ''}</p>` : ''}
          ${sellable
            ? `<a class="ss-button" href="/products/${p.smartsenses_brand_name ? this.slug(p.smartsenses_brand_name) : p.id}">${T('view_at_smartsenses')}</a>`
            : `<p class="ss-configurator__result-na"><em>${T('not_in_range')}</em></p>`}
        </div>
      </li>
    `;
  }

  renderNav(step, total) {
    return `
      <div class="ss-configurator__nav">
        ${step > 0 ? `<button type="button" class="ss-button ss-button--ghost" data-action="back">${T('back')}</button>` : '<span></span>'}
        <button type="button" class="ss-button" data-action="next">${step === total - 1 ? T('show_recommendation') : T('next')}</button>
      </div>
    `;
  }

  wireEvents() {
    this.querySelectorAll('[data-set]').forEach((el) => {
      el.addEventListener('click', (e) => {
        if (el.dataset.value) {
          this.state[el.dataset.set] = el.dataset.value;
        } else if (el.tagName === 'INPUT') {
          this.state[el.dataset.set] = el.checked;
        }
        this.render();
      });
      el.addEventListener('change', (e) => {
        if (el.tagName === 'INPUT' && el.type === 'checkbox') {
          this.state[el.dataset.set] = el.checked;
        }
      });
    });
    this.querySelectorAll('[data-musthave]').forEach((el) => {
      el.addEventListener('change', () => {
        this.state.mustHave[el.dataset.musthave] = el.checked;
      });
    });
    const budget = this.querySelector('[data-budget]');
    if (budget) {
      budget.addEventListener('input', () => {
        this.state.budgetMax = parseInt(budget.value, 10);
        const out = this.querySelector('.ss-configurator__slider-value');
        if (out) out.innerHTML = T('budget_up_to_html', { amount: this.state.budgetMax });
      });
    }
    this.querySelectorAll('[data-budget-quick]').forEach((b) => {
      b.addEventListener('click', () => {
        this.state.budgetMax = parseInt(b.dataset.budgetQuick, 10);
        this.render();
      });
    });
    this.querySelectorAll('[data-action]').forEach((b) => {
      b.addEventListener('click', () => this.handleAction(b.dataset.action));
    });
  }

  handleAction(a) {
    if (a === 'next') {
      if (this.state.step < 5) this.state.step += 1;
      this.render();
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (a === 'back') {
      if (this.state.step > 0) this.state.step -= 1;
      this.render();
    } else if (a === 'restart') {
      this.state.step = 0;
      this.render();
    } else if (a === 'widen') {
      this.state.onlySellable = false;
      this.render();
    }
  }

  esc(s) { return String(s == null ? '' : s).replace(/[<>&"]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' })[c]); }
  slug(s) { return String(s).toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-|-$/g, ''); }
}

if (!customElements.get('ss-configurator')) {
  customElements.define('ss-configurator', SsConfigurator);
}
